import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { stringLength } from "@firebase/util";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Define specifications for each sub-form, excluding uploadPhoto and zip_url
const subFormSpecifications = {
  AdhesiveSealant: [
    "expiryMonitoring",
    "lotNo",
    "make",
    "manufacturingDate",
    "model",
    "remarks",
  ],
  Cell: [
    "efficiency",
    "expiryMonitoring",
    "lotNo",
    "make",
    "manufacturingDate",
    "remarks",
    "wattPeak",
  ],
  Frame: ["lotNo", "make", "size", "type"],
  cellFixingType: ["lotNo", "make", "size", "type"],
  evaBack: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  evaFront: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  flux: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model"],
  glassBack: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  glassFront: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "model", "size"],
  interconnector: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "size"],
  junctionBox: ["cableCrossSectionLength", "diodeNo", "ipRating", "jbLipHeight", "lotNo", "make", "model"],
  pottingMaterial: ["expiryMonitoringA", "expiryMonitoringB", "lotNo", "make", "manufacturingDateA", "manufacturingDateB", "model"],
  stringConnector: ["expiryMonitoring", "lotNo", "make", "manufacturingDate", "size"],
};

// Human-readable labels for specifications
const specificationLabels = {
  expiryMonitoring: "Expiry Monitoring",
  expiryMonitoringA: "Expiry Monitoring A",
  expiryMonitoringB: "Expiry Monitoring B",
  lotNo: "Lot No",
  make: "Make",
  manufacturingDate: "Manufacturing Date",
  manufacturingDateA: "Manufacturing Date A",
  manufacturingDateB: "Manufacturing Date B",
  model: "Model",
  remarks: "Remarks",
  efficiency: "Efficiency",
  wattPeak: "Watt Peak",
  size: "Size",
  type: "Type",
  cableCrossSectionLength: "Cable Cross Section Length",
  diodeNo: "Diode No",
  ipRating: "IP Rating",
  jbLipHeight: "JB Lip Height",
  zip_url: "Zip URL",
};

// Human-readable labels for sub-forms
const subFormLabels = {
  AdhesiveSealant: "Adhesive Sealant (BOM)",
  Cell: "Cell (BOM)",
  Frame: "Frame (BOM)",
  cellFixingType: "Cell Fixing Type (BOM)",
  evaBack: "EVA Back (BOM)",
  evaFront: "EVA Front (BOM)",
  flux: "Flux (BOM)",
  glassBack: "Glass Back (BOM)",
  glassFront: "Glass Front (BOM)",
  interconnector: "Interconnector (BOM)",
  junctionBox: "Junction Box (BOM)",
  pottingMaterial: "Potting Material (BOM)",
  stringConnector: "String Connector (BOM)",
};

const BOMTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Define static table headers
  const staticHeaders = [
    { id: "subForm", label: "Sub-form" },
    { id: "specification", label: "Specification" },
  ];

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();

  // Create dynamic headers for all dates and shifts
  const dynamicHeaders = dates.flatMap((date) =>
    effectiveShifts.map((shift) => ({
      id: `${date}_${shift}`,
      label: `${date} - ${shift}`,
      date,
      shift,
    }))
  );

  // Calculate column widths based on content
  const columnWidths = useMemo(() => {
    const widths = {
      subForm: estimateTextWidth("Sub-form"),
      specification: estimateTextWidth("Specification"),
    };

    // Calculate widths for sub-forms and specifications
    Object.entries(subFormSpecifications).forEach(([subFormKey, specs]) => {
      if (subForm.length === 0 || subForm.includes(subFormLabels[subFormKey])) {
        const subFormLabel = subFormLabels[subFormKey] || subFormKey;
        widths.subForm = Math.min(
          Math.max(widths.subForm, estimateTextWidth(subFormLabel)),
          8 * window.innerWidth / 100
        );
        specs.forEach((spec) => {
          const specLabel = specificationLabels[spec] || spec;
          widths.specification = Math.min(
            Math.max(widths.specification, estimateTextWidth(specLabel)),
            8 * window.innerWidth / 100
          );
        });
        // Add width for zip_url specification
        widths.specification = Math.min(
          Math.max(widths.specification, estimateTextWidth(specificationLabels.zip_url)),
          8 * window.innerWidth / 100
        );
      }
    });

    // Calculate widths for data columns
    dynamicHeaders.forEach((header) => {
      widths[header.id] = 100; // Default width
      data.forEach((item) => {
        Object.entries(item.form_data || {}).forEach(([subFormKey, subFormData]) => {
          if (subForm.length === 0 || subForm.includes(subFormLabels[subFormKey])) {
            Object.entries(subFormData).forEach(([key, value]) => {
              if (key !== "uploadPhoto") {
                widths[header.id] = Math.max(
                  widths[header.id],
                  estimateTextWidth(String(value))
                );
              }
            });
            // Account for zip_url width
            const zipUrlValue = item.zip_url && item.zip_url.trim() !== "" ? "LINK" : "N/A";
            widths[header.id] = Math.max(
              widths[header.id],
              estimateTextWidth(zipUrlValue)
            );
          }
        });
      });
    });

    return widths;
  }, [data, subForm]);

  // Calculate cumulative left positions for sticky columns
  const getLeftPosition = (index) => {
    const staticKeys = ["subForm", "specification"];
    return staticKeys
      .slice(0, index)
      .reduce((sum, key) => sum + columnWidths[key], 0);
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const rows = [];
    Object.entries(subFormSpecifications).forEach(([subFormKey, specs]) => {
      if (subForm.length === 0 || subForm.includes(subFormLabels[subFormKey])) {
        const subFormLabel = subFormLabels[subFormKey] || subFormKey;

        // Add rows for regular specifications
        specs.forEach((spec) => {
          const row = {
            subForm: subFormLabel,
            specification: specificationLabels[spec] || spec,
            data: {},
          };

          // Initialize data for all possible date-shift combinations
          dynamicHeaders.forEach((header) => {
            row.data[header.id] = "N/A"; // Default value
          });

          // Populate data for this specification
          data.forEach((item) => {
            if (item.form_data?.[subFormKey]) {
              const key = `${formatDate(item.inspectionDate)}_${item.inspectionShift}`;
              if (dynamicHeaders.some((header) => header.id === key)) {
                row.data[key] = item.form_data[subFormKey][spec] || "N/A";
              }
            }
          });

          rows.push(row);
        });

        // Add zip_url row for this sub-form
        const zipRow = {
          subForm: subFormLabel,
          specification: specificationLabels.zip_url,
          data: {},
        };

        // Initialize data for all possible date-shift combinations
        dynamicHeaders.forEach((header) => {
          zipRow.data[header.id] = "N/A"; // Default value
        });

        // Populate zip_url data
        data.forEach((item) => {
          if (item.sub_form === subFormKey || item.form_data?.[subFormKey]) {
            const key = `${formatDate(item.inspectionDate)}_${item.inspectionShift}`;
            if (dynamicHeaders.some((header) => header.id === key)) {
              zipRow.data[key] = item.zip_url && item.zip_url.trim() !== "" ? item.zip_url : "N/A";
            }
          }
        });

        rows.push(zipRow);
      }
    });
    return rows;
  }, [data, subForm, dynamicHeaders]);
  
  // Filter data based on date range and shifts
  const filteredDynamicHeaders = dynamicHeaders.filter((header) => {
    const dateInRange =
      (!startDate || header.date >= formatDate(startDate)) &&
      (!endDate || header.date <= formatDate(endDate));
    const shiftMatch = effectiveShifts.includes(header.shift);
    return dateInRange && shiftMatch;
  });

  const exportToExcel = () => {
    const headerRow1 = [
      "Sub-form",
      "Specification",
      ...filteredDynamicHeaders.map((h) => h.label),
    ];

    const excelRows = tableRows.map((row) => {
      const rowData = [
        row.subForm,
        row.specification,
        ...filteredDynamicHeaders.map((header) => {
          const value = row.data[header.id];

          if (row.specification === specificationLabels.zip_url && value !== "N/A") {
            return `=HYPERLINK("${value}", "LINK")`;
          }
          return value;
        }),
      ];
      return rowData;
    });

    const ws = XLSX.utils.aoa_to_sheet([headerRow1, ...excelRows]);

    const colWidths = headerRow1.map((_, colIndex) => {
      let maxLength = 10; // Minimum Length
      excelRows.forEach((r) => {
        const cellValue = r[colIndex] ? String(r[colIndex]) : "";
        const length = cellValue.startsWith("=HYPERLINK") ? 4 : cellValue.length;
        if (length > maxLength) maxLength = length;
      });
      return { wch: Math.min(maxLength + 2, 50) }; // Cap width at 50
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BOM Verification");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fileName = `BOM_Verification_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Bill of Material Verification
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0}
      >
        Download as Excel
      </Button>
      
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              {staticHeaders.map((header, index) => (
                <StickyTableCell
                  key={header.id}
                  rowSpan={2}
                  columnWidth={columnWidths[header.id]}
                  left={getLeftPosition(index)}
                  isLastSticky={index === staticHeaders.length - 1}
                >
                  {header.label}
                </StickyTableCell>
              ))}
              {filteredDynamicHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  columnWidth={columnWidths[header.id]}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={staticHeaders.length + filteredDynamicHeaders.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.subForm}
                  left={getLeftPosition(0)}
                  isLastSticky={false}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.subForm}
                    left={getLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.subForm}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.specification}
                    left={getLeftPosition(1)}
                    isLastSticky={true}
                  >
                    {row.specification}
                  </StickyBodyTableCell>
                  {filteredDynamicHeaders.map((header) => (
                    <BodyTableCell
                      key={`${header.id}_${index}`}
                      columnWidth={columnWidths[header.id]}
                    >
                      {row.specification === specificationLabels.zip_url && row.data[header.id] !== "N/A" ? (
                        <a
                          href={row.data[header.id]}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          LINK
                        </a>
                      ) : (
                        row.data[header.id]
                      )}
                    </BodyTableCell>
                  ))}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default BOMTable;